import { endpoints } from '../config/endpoints';
import { apiRequest } from './api-client';
import type {
  Connector,
  ConnectorTool,
  ConnectorTemplate,
  ConnectorListResponse,
  TemplateListResponse,
  TestConnectionResult
} from './connector-types';

// ─── Connectors ──────────────────────────────

export async function listConnectors(params?: {
  type?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<ConnectorListResponse> {
  const qs = new URLSearchParams();
  if (params?.type) qs.set('type', params.type);
  if (params?.status) qs.set('status', params.status);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  const query = qs.toString();
  const result = await apiRequest<any>(`${endpoints.connectors.list()}${query ? `?${query}` : ''}`);
  // Backend may return { data: [...], pagination } or { connectors: [...], total }
  const connectors = result.connectors ?? result.data ?? [];
  const total = result.pagination?.totalCount ?? result.total ?? connectors.length;
  return { connectors, total, limit: params?.limit ?? 100, offset: params?.offset ?? 0 };
}

export async function getConnector(id: string): Promise<Connector> {
  return apiRequest(endpoints.connectors.getById(id));
}

export async function createConnector(data: {
  name: string;
  baseUrl: string;
  workspaceId: string;
  authType: 'api_key' | 'oauth2' | 'none';
  description?: string;
  headers?: Record<string, string>;
  authProviderId?: number;
}): Promise<Connector> {
  return apiRequest(endpoints.connectors.create(), {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

export async function updateConnector(id: string, data: {
  name?: string;
  description?: string;
  baseUrl?: string;
  headers?: Record<string, string>;
  status?: string;
}): Promise<Connector> {
  return apiRequest(endpoints.connectors.update(id), {
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

export async function deleteConnector(id: string): Promise<void> {
  return apiRequest(endpoints.connectors.delete(id), { method: 'DELETE' });
}

export async function getConnectorTools(connectorId: string): Promise<{ tools: ConnectorTool[] }> {
  const result = await apiRequest<any>(endpoints.connectors.tools(connectorId));
  return { tools: result.tools ?? result.data ?? [] };
}

export async function discoverTools(connectorId: string): Promise<{ tools: ConnectorTool[] }> {
  const result = await apiRequest<any>(endpoints.connectors.discoverTools(connectorId));
  return { tools: result.tools ?? result.data ?? [] };
}

export async function testConnection(connectorId: string): Promise<TestConnectionResult> {
  return apiRequest(endpoints.connectors.test(connectorId), { method: 'POST' });
}

export async function getConnectorAgents(connectorId: string): Promise<{ agents: { agentId: string; agentName: string }[] }> {
  const result = await apiRequest<any>(endpoints.connectors.agents(connectorId));
  const raw = result.agents ?? result.data ?? result.assistants ?? [];
  const agents = Array.isArray(raw)
    ? raw.map((item: any) => ({
        agentId: String(item.agentId ?? item.id ?? item.assistantId ?? ''),
        agentName: item.agentName ?? item.name ?? item.assistantName ?? 'Untitled agent',
      }))
    : [];
  return { agents };
}

// ─── Templates ──────────────────────────────

export async function listTemplates(params?: {
  search?: string;
  type?: string;
  isFeatured?: boolean;
  limit?: number;
  offset?: number;
}): Promise<TemplateListResponse> {
  const qs = new URLSearchParams();
  if (params?.search) qs.set('search', params.search);
  if (params?.type) qs.set('type', params.type);
  if (params?.isFeatured !== undefined) qs.set('isFeatured', String(params.isFeatured));
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.offset) qs.set('offset', String(params.offset));
  const query = qs.toString();
  const result = await apiRequest<any>(`${endpoints.connectorTemplates.list()}${query ? `?${query}` : ''}`);
  // Backend may return { data: [...], pagination } or { templates: [...], total }
  const templates = result.templates ?? result.data ?? [];
  const total = result.pagination?.totalCount ?? result.total ?? templates.length;
  return { templates, total, limit: params?.limit ?? 100, offset: params?.offset ?? 0 };
}

export async function getTemplate(id: string): Promise<ConnectorTemplate> {
  return apiRequest(endpoints.connectorTemplates.getById(id));
}

export async function instantiateTemplate(
  templateId: string,
  data: { name?: string; description?: string; config: Record<string, string | number | boolean | null> }
): Promise<Connector> {
  return apiRequest(endpoints.connectorTemplates.instantiate(templateId), {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

// ─── Session Connectors ─────────────────────

export async function getSessionConnectors(sessionId: string): Promise<{ connectors: Connector[] }> {
  const result = await apiRequest<any>(endpoints.sessions.connectors(sessionId));
  return { connectors: result.connectors ?? result.data ?? [] };
}

export async function attachConnectorToSession(
  sessionId: string,
  connectorId: string,
  config?: Record<string, unknown>
): Promise<{ attached: boolean; alreadyExists: boolean }> {
  return apiRequest(endpoints.sessions.connectors(sessionId), {
    method: 'POST',
    body: JSON.stringify({ connectorId, config })
  });
}

export async function detachConnectorFromSession(
  sessionId: string,
  connectorId: string
): Promise<void> {
  return apiRequest(endpoints.sessions.connector(sessionId, connectorId), {
    method: 'DELETE'
  });
}
